import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DialogContent from "@mui/material/DialogContent";
import FormHelperText from "@mui/material/FormHelperText";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { WalletPaymentModel } from "_models/data/wallet/data.walletPayment.model";
import { WalletPaymentErrorModel } from "_models/errors/wallet/error.walletPayment.model";
import OtpInput from "component/_common/forms/otpInput";
import { AppStatusCode } from "config/appStatusCode";
import { HTTP_ERROR } from "functions/http";
import { WalletPaymentSubmit } from "functions/http-requests/wallet";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setAlert } from "state/slice/alert";
import { toggleLoading } from "state/slice/loading";
import { RootState } from "state/store";
import { validateWalletPaymentSubmit } from "./formValidator";

interface WalletPaymentMethodProps {
  handleRefresh?: () => void;
  handleClose: () => void;
  walletPaymentState: WalletPaymentModel;
  handleContinue?: (ID: string) => void;
}

const WalletPaymentMethod: React.FC<WalletPaymentMethodProps> = ({
  handleRefresh,
  handleClose,
  walletPaymentState,
  handleContinue,
}) => {
  const { palette } = useTheme();
  const Dispatch = useDispatch();

  const { userInfo } = useSelector((state: RootState) => state.user);

  const [state, setState] = useState<WalletPaymentModel>(
    new WalletPaymentModel()
  );
  const [errors, setErrors] = useState<WalletPaymentErrorModel>(
    new WalletPaymentErrorModel()
  );

  const handleChange = (newValue: string) => {
    setState((prev) => ({ ...prev, PIN: newValue }));
  };

  const handleWalletPaymentSubmit = () => {
    Dispatch(toggleLoading(true));
    WalletPaymentSubmit({
      DATA: {
        walletPaymentId: state.walletPaymentId || "",
        PIN: state.PIN || "",
      },
    })
      .then((res) => {
        if (res?.data?.statusCode === AppStatusCode.api_success) {
          if (handleContinue && state?.rechargeId) {
            handleContinue(state.rechargeId);
          } else {
            Dispatch(
              setAlert({
                type: res?.data?.level || "error",
                message: res?.data?.message,
              })
            );
            if (handleRefresh) handleRefresh();
            handleClose();
            Dispatch(toggleLoading(false));
          }
        } else {
          Dispatch(toggleLoading(false));
          Dispatch(
            setAlert({
              type: res?.data?.level || "error",
              message: res?.data?.message,
            })
          );
        }
      })
      .catch((error) => {
        Dispatch(setAlert({ type: "error", message: HTTP_ERROR(error) }));
        Dispatch(toggleLoading(false));
      });
  };

  const handleSubmit = (): void => {
    let ValidateStep: {
      valid: boolean;
      errors: { name: string; error: string }[];
    } = validateWalletPaymentSubmit(state);

    if (!ValidateStep?.valid) {
      for (
        let i = 0, item: { name: string; error: string };
        !!(item = ValidateStep.errors[i++]);

      ) {
        setErrors((prevState) => ({ ...prevState, [item.name]: item.error }));
      }
      return;
    }

    if (!state.walletPaymentId) {
      Dispatch(setAlert({ message: "Payment Id not found", type: "error" }));
      return;
    }

    handleWalletPaymentSubmit();
  };

  useEffect(() => {
    if (errors?.PIN && state?.PIN?.trim()) {
      setErrors((prev) => ({ ...prev, PIN: "" }));
    }
  }, [state.PIN, errors.PIN]);

  useEffect(() => {
    if (userInfo?.userId) {
      setState((prev) => ({ ...prev, roleId: userInfo.userId }));
    }
  }, [userInfo]);

  useEffect(() => {
    setState((prev) => ({ ...prev, ...walletPaymentState }));
  }, [walletPaymentState]);

  return (
    <>
      <DialogContent>
        <Box
          sx={{
            margin: "auto",
            textAlign: "center",
            maxWidth: 400,
            p: 3,
          }}
        >
          <Typography
            variant="h2"
            fontSize={36}
            fontWeight={700}
            textAlign="center"
            sx={{ textTransform: "capitalize" }}
          >
            Verify Wallet
          </Typography>

          <Typography>Enter your 6 digit Wallet PIN & Verify</Typography>

          <Box>
            <OtpInput
              code={state.PIN || ""}
              handleChange={handleChange}
              maximumLength={6}
            />

            {errors?.PIN ? (
              <Box pt={1}>
                <FormHelperText
                  sx={{ color: palette.error.main, textAlign: "center" }}
                >
                  {errors.PIN}
                </FormHelperText>
              </Box>
            ) : (
              <></>
            )}

            <Box pt={5} display="flex" flexDirection="column" gap={1}>
              <Button
                onClick={handleSubmit}
                fullWidth
                variant="contained"
                disableElevation
                disabled={
                  state?.PIN && state?.PIN?.trim()?.length === 6 ? false : true
                }
                sx={{
                  textTransform: "capitalize",
                  py: "12px",
                }}
              >
                Continue
              </Button>
              <Button
                onClick={handleClose}
                fullWidth
                variant="text"
                disableElevation
                color="inherit"
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </>
  );
};

export default WalletPaymentMethod;
